'use client';

import { useState } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { InformationCircleIcon } from '@terra/ui-icons';
import { AlertBanner } from '../../../components/alert-banner';
import { Dialog } from '../../../components/dialog';
import { Field } from '../../../components/field';
import { Textarea } from '../../../components/textarea';
import { Tooltip } from '../../../components/tooltip';
import { IconButton } from '../../../components/icon-button';
import { BodyStrong } from '../../../components/typography';
import type { RerunUnverifiedAssetBodyDto } from '@terra/shared-contracts-assets';
import { UnverifiedAssetLockedEndpointField } from './UnverifiedAssetLockedEndpointField';

export type UnverifiedAssetRerunContextValues = RerunUnverifiedAssetBodyDto;

type UnverifiedAssetRerunContextDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (
    context: UnverifiedAssetRerunContextValues,
  ) => void | Promise<void>;
  method: string;
  endpoint: string;
  isSubmitting?: boolean;
};

const EMPTY_CONTEXT: UnverifiedAssetRerunContextValues = {
  instructions: '',
  parameters: '',
  harData: '',
  usageExample: '',
};

type ContextFieldKey = keyof UnverifiedAssetRerunContextValues;

const CONTEXT_FIELDS: {
  key: ContextFieldKey;
  labelKey:
    | 'rerunContextInstructionsLabel'
    | 'rerunContextParametersLabel'
    | 'rerunContextHarLabel'
    | 'rerunContextUsageExampleLabel';
  tooltipKey:
    | 'rerunContextInstructionsTooltip'
    | 'rerunContextParametersTooltip'
    | 'rerunContextHarTooltip'
    | 'rerunContextUsageExampleTooltip';
  placeholderKey:
    | 'rerunContextInstructionsPlaceholder'
    | 'rerunContextParametersPlaceholder'
    | 'rerunContextHarPlaceholder'
    | 'rerunContextUsageExamplePlaceholder';
}[] = [
  {
    key: 'instructions',
    labelKey: 'rerunContextInstructionsLabel',
    tooltipKey: 'rerunContextInstructionsTooltip',
    placeholderKey: 'rerunContextInstructionsPlaceholder',
  },
  {
    key: 'parameters',
    labelKey: 'rerunContextParametersLabel',
    tooltipKey: 'rerunContextParametersTooltip',
    placeholderKey: 'rerunContextParametersPlaceholder',
  },
  {
    key: 'harData',
    labelKey: 'rerunContextHarLabel',
    tooltipKey: 'rerunContextHarTooltip',
    placeholderKey: 'rerunContextHarPlaceholder',
  },
  {
    key: 'usageExample',
    labelKey: 'rerunContextUsageExampleLabel',
    tooltipKey: 'rerunContextUsageExampleTooltip',
    placeholderKey: 'rerunContextUsageExamplePlaceholder',
  },
];

export function UnverifiedAssetRerunContextDialog({
  open,
  onOpenChange,
  onConfirm,
  method,
  endpoint,
  isSubmitting = false,
}: UnverifiedAssetRerunContextDialogProps) {
  const t = useTranslations('designSystem.applicationAssets');
  const tCommon = useTranslations('designSystem.common');
  const [values, setValues] =
    useState<UnverifiedAssetRerunContextValues>(EMPTY_CONTEXT);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isSubmitting) {
      return;
    }

    if (!nextOpen) {
      setValues(EMPTY_CONTEXT);
    }

    onOpenChange(nextOpen);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      size="md"
      title={t('rerunContextTitle')}
      description={t('rerunContextDescription')}
      closeLabel={tCommon('close')}
      lazyMount
      unmountOnExit
      secondaryAction={{
        label: tCommon('cancel'),
        onClick: () => handleOpenChange(false),
        disabled: isSubmitting,
      }}
      primaryAction={{
        label: t('actions.rerun'),
        loading: isSubmitting,
        disabled: isSubmitting,
        onClick: async () => {
          await onConfirm(values);
        },
      }}
    >
      <VStack gap="3" alignItems="stretch" w="full">
        <AlertBanner
          kind="attention"
          description={t('rerunContextGuardrailWarning')}
        />
        <UnverifiedAssetLockedEndpointField
          method={method}
          endpoint={endpoint}
          label={t('rerunContextEndpointLabel')}
          helpText={t('rerunContextEndpointTooltip')}
        />
        <VStack gap="2" alignItems="stretch" w="full">
          <BodyStrong color="fg.default">
            {t('rerunContextAdditionalContext')}
          </BodyStrong>
          {CONTEXT_FIELDS.map((field) => (
            <Field.Root key={field.key} w="full">
              <HStack gap="1.5" alignItems="center">
                <Field.Label mb="0">{t(field.labelKey)}</Field.Label>
                <Tooltip title={t(field.tooltipKey)}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    aria-label={t('rerunContextFieldHelp')}
                  >
                    <InformationCircleIcon size={14} />
                  </IconButton>
                </Tooltip>
              </HStack>
              <Textarea
                value={values[field.key] ?? ''}
                onChange={(event) =>
                  setValues((previous) => ({
                    ...previous,
                    [field.key]: event.target.value,
                  }))
                }
                placeholder={t(field.placeholderKey)}
                rows={2}
                disabled={isSubmitting}
              />
            </Field.Root>
          ))}
        </VStack>
      </VStack>
    </Dialog>
  );
}
